import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

type Props = {
  isOpen: boolean;
  title?: string;
  children?: any;
  primaryText?: string;
  primaryLoading?: boolean;
  disablePrimary?: boolean;
  secondaryText?: string;
  secondaryLoading?: boolean;
  disableSecondary?: boolean;
  disableWhenLoading?: boolean;
  isFullScreen?: boolean;
  isNonScrollable?: boolean;
  contentContainerClassName?: string;
  smallHeader?: boolean;
  onClose?: () => void;
  onPrimary?: () => void;
  onSecondary?: () => void;
};

export const Modal = ({
  isOpen,
  title,
  children,
  primaryText,
  primaryLoading,
  secondaryText,
  secondaryLoading,
  disableWhenLoading,
  disablePrimary,
  disableSecondary,
  isFullScreen,
  isNonScrollable,
  contentContainerClassName,
  smallHeader,
  onClose,
  onPrimary,
  onSecondary,
}: Props) => {
  const firstRender = useRef(true);
  const container = useMemo(() => document.createElement("div"), []);

  useEffect(() => {
    document.body?.appendChild(container);
    return () => {
      document.body?.removeChild(container);
    };
  }, [container]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      if (!isOpen) return;
    }

    if (isOpen) {
      if (document.body.parentElement)
        document.body.parentElement.style.overflow = `hidden`;
    }

    return () => {
      if (document.body.parentElement)
        document.body.parentElement.style.overflow = "";
    };
  }, [isOpen]);

  container.className = clsx("modal", {
    "is-active": isOpen,
    "is-fullscreen": isFullScreen,
    "no-scrollable": isNonScrollable,
  });

  const modal = (
    <>
      <div className="modal-background" />
      <div className="modal-card">
        <header
          className="modal-card-head"
          style={smallHeader ? { height: "1rem" } : {}}
        >
          <p className="modal-card-title has-ellipsis-if-long">{title}</p>
          {!!onClose && (
            <button
              type="button"
              className="delete"
              aria-label="close"
              disabled={
                disableWhenLoading && (primaryLoading || secondaryLoading)
              }
              onClick={onClose}
            />
          )}
        </header>
        {children && (
          <section
            className={clsx("modal-card-body", contentContainerClassName)}
          >
            {children}
          </section>
        )}

        {(!!primaryText || !!secondaryText) && (
          <footer className="modal-card-foot">
            {!!primaryText && (
              <button
                type="button"
                className={clsx("button", "is-primary", {
                  "is-loading": primaryLoading,
                })}
                disabled={
                  disablePrimary || (disableWhenLoading && secondaryLoading)
                }
                onClick={onPrimary}
              >
                {primaryText}
              </button>
            )}

            {!!secondaryText && (
              <button
                type="button"
                className={clsx("button", {
                  "is-loading": secondaryLoading,
                })}
                disabled={
                  disableSecondary || (disableWhenLoading && primaryLoading)
                }
                onClick={onSecondary}
              >
                {secondaryText}
              </button>
            )}
          </footer>
        )}
      </div>
    </>
  );

  return createPortal(modal, container);
};
